import {
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Icon,
    Input,
    Radio,
    RadioGroup,
    Select,
    Spinner,
    Stack,
    Text,
    useColorModeValue,
    useDisclosure
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, useMap, Marker, Popup } from "react-leaflet";
import brazilGeoJson from "../../assets/brazil";
import StationsApi from "Api/StationsApi";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { corChuva, corTemperatura, corUmidade } from "../Public/Home";
import { divIcon } from "leaflet";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import PrintMode from "components/Buttons/PrintMode";
import { TbFlame } from "react-icons/tb";

export default function Heatmap() {
    const [stationsData, setStationsData] = useState([]);
    const [isLoadingStations, setIsLoadingStations] = useState(false);
    const [heatOptions, setHeatOptions] = useState({
        resolution: "5",
        opacity: "0.6",
        exp: "3",
        show_stations: "1",
    });
    const [dataOptions, setDataOptions] = useState({
        currentDataType: "temp_avg",
        min_temp: null,
        max_temp: null,
        date_start: null,
        date_end: null,
    });

    const [heatType, setHeatType] = useState("temp_avg");
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isLoadingHeat, setLoadingHeat] = useState(false);

    const {
        isOpen: isOpenData,
        onOpen: onOpenData,
        onClose: onCloseData,
    } = useDisclosure();

    window.mapMarkerType = heatOptions.currentDataType;

    let center = [-14.4095261, -51.31668];

    async function loadStationsData(data) {
        setIsLoadingStations(true);
        let sData = await StationsApi.heatMap(data);
        setStationsData(sData.json || []);
        setIsLoadingStations(false);
    }

    function getBadgeColor(value) {
        switch (heatOptions.currentDataType) {
            case 'temp_avg':
            case 'temp_min':
            case 'temp_max':
                return corTemperatura(value);
            case 'humidity_avg':
            case 'humidity_min':
            case 'humidity_max':

                return corUmidade(value);
            case 'rain':
                return corChuva(value);
            case 'cold_hours':
                let data = stationsData.map((s) => s.value);
                let max = Math.max(...data);
                let min = Math.min(...data);

                // Calculating the percentage of the value in the range, and return a value from 0 to 1, in steps of 0.1
                let m = ((value - min) / (max - min)).toFixed(1);

                return {
                    0.0: "#FFFFFF",    // "#FFFFFF";
                    0.1: "#63B3ED",    // "#EC743B";
                    0.2: "#63B3ED",    // "#EC743B";
                    0.3: "#4299E1",    // "#EAB735";
                    0.4: "#3182CE",    // "#51A14F";
                    0.5: "#2B6CB0",    // "#4FAEAD";
                    0.6: "#2C5282",    // "#2682C5";
                    0.7: "#2A4365",    // "#2D458F";
                    0.8: "#1A365D",    // "#694295";
                    0.9: "#44337A",    // "#575757";
                    1.0: "#322659"     // "#1F1F1F";
                }[m];
        }
    }

    function renderStationMarkers() {
        return (stationsData || []).map((station) => (
            <Marker
                icon={divIcon({
                    iconSize: [0, 0],
                    html: `<div style="background-color: ${getBadgeColor(station.value)}; color:white;" class="icone icone-${getBadgeColor(station.value).replace("#", "")}">${station.value || "-"}</div>`,
                    popupAnchor: [5, 0],
                    className: `${station.value || "x"}`,
                })}
                key={"marker_" + station.id}
                position={[station.lat, station.long]}
            >
                <Popup>
                    <Flex justifyContent={"center"} w="100%" direction={"column"}>
                        <Text
                            textAlign={"center"}
                            fontWeight={"semibold"}
                            m={"0 !important"}
                        >
                            {station.full_name}
                        </Text>
                    </Flex>
                </Popup>
            </Marker>
        ));
    }

    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");

    return (
        <>
            <Box bg={cardBg} borderRadius="xl" overflow="hidden" boxShadow="md" border="1px solid" borderColor={borderColor}>
                <Box bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)" py={5} px={6}>
                    <Flex align="center" gap={3} justify="space-between" flexWrap="wrap">
                        <Flex align="center" gap={3}>
                            <Flex align="center" justify="center" bg="whiteAlpha.200" borderRadius="lg" p={2}>
                                <Icon as={TbFlame} color="white" boxSize={5} />
                            </Flex>
                            <Box>
                                <Text color="white" fontWeight="bold" fontSize="xl">Mapa de Calor</Text>
                                <Text color="whiteAlpha.800" fontSize="sm">Visualização Climática</Text>
                            </Box>
                        </Flex>

                        <Flex flexWrap="wrap" gridGap={2}>
                            <Button
                                onClick={() => {
                                    var evt = new CustomEvent("generateheatmap", {
                                        detail: {
                                            currentDataType: heatOptions.currentDataType,
                                            resolution: heatOptions.resolution,
                                            opacity: heatOptions.opacity,
                                            exp: heatOptions.exp,
                                        },
                                    });

                                    window.dispatchEvent(evt);
                                }}
                                size="sm"
                                colorScheme="whiteAlpha"
                                color="white"
                                variant="solid"
                                isLoading={isLoadingHeat}
                                isDisabled={!stationsData.length}
                                loadingText="Gerando..."
                                borderRadius="lg"
                            >
                                Gerar Mapa de Calor
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onOpenData}
                                isDisabled={isLoadingHeat}
                                borderRadius="lg"
                                borderColor="whiteAlpha.600"
                                color="white"
                                _hover={{ bg: "whiteAlpha.200" }}
                            >
                                Dados
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onOpen}
                                isDisabled={isLoadingHeat}
                                borderRadius="lg"
                                borderColor="whiteAlpha.600"
                                color="white"
                                _hover={{ bg: "whiteAlpha.200" }}
                            >
                                Configurações
                            </Button>
                        </Flex>
                    </Flex>
                </Box>
                <Box p={6}>
                    <link
                        rel="stylesheet"
                        href="https://unpkg.com/react-leaflet-markercluster/dist/styles.min.css"
                    />

                    <Drawer isOpen={isOpenData} placement="right" onClose={onCloseData}>
                        <DrawerOverlay backdropFilter="blur(4px)" />
                        <DrawerContent>
                            <DrawerCloseButton />
                            <Box bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)" py={4} px={6} mb={4}>
                                <Flex align="center" gap={3}>
                                    <Flex align="center" justify="center" bg="whiteAlpha.200" borderRadius="lg" p={2}>
                                        <Icon as={TbFlame} color="white" boxSize={5} />
                                    </Flex>
                                    <Text color="white" fontWeight="bold" fontSize="lg">Dados do Mapa</Text>
                                </Flex>
                            </Box>

                            <DrawerBody>
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();

                                        var formData = new FormData(e.target);
                                        var object = {};
                                        formData.forEach((value, key) => (object[key] = value));

                                        onCloseData();

                                        setDataOptions(object);
                                        loadStationsData(object);
                                    }}
                                >
                                    <Text fontWeight={"semibold"}>Dado a ser exibido</Text>
                                    <RadioGroup
                                        defaultValue={dataOptions.currentDataType}
                                        mt={2}
                                        name="currentDataType"
                                        onChange={(e) => setHeatType(e)}
                                    >
                                        <Stack direction="column">
                                            <Radio value="temp_avg">Temperatura Média</Radio>
                                            <Radio value="temp_min">Temperatura Mínima</Radio>
                                            <Radio value="temp_max">Temperatura Máxima</Radio>
                                            <Radio value="humidity_avg">Umidade Média</Radio>
                                            <Radio value="humidity_min">Umidade Mínima</Radio>
                                            <Radio value="humidity_max">Umidade Máxima</Radio>
                                            <Radio value="rain">Chuva</Radio>
                                            <Radio value="cold_hours">Horas Frio</Radio>
                                        </Stack>
                                    </RadioGroup>

                                    {heatType == "cold_hours" && (
                                        <>
                                            <Text mt={4} fontWeight={"semibold"}>
                                                Acima de °C
                                            </Text>
                                            <Input
                                                type="number"
                                                name="min_temp"
                                                isRequired
                                                defaultValue={dataOptions.min_temp}
                                            />


                                            <Text mt={4} fontWeight={"semibold"}>
                                                Abaixo de °C
                                            </Text>
                                            <Input
                                                type="number"
                                                name="max_temp"
                                                isRequired
                                                defaultValue={dataOptions.max_temp}
                                            />
                                        </>
                                    )}

                                    <Text mt={4} fontWeight={"semibold"}>
                                        A partir de
                                    </Text>
                                    <Input
                                        type="date"
                                        name="date_start"
                                        isRequired
                                        defaultValue={dataOptions.date_start}
                                    />

                                    <Text mt={4} fontWeight={"semibold"}>
                                        Até
                                    </Text>
                                    <Input
                                        type="date"
                                        name="date_end"
                                        isRequired
                                        defaultValue={dataOptions.date_end}
                                    />

                                    <Flex mt={4} justify={"center"}>
                                        <Box>
                                            <Button colorSchema="blue" type="submit">
                                                Carregar dados
                                            </Button>
                                        </Box>
                                    </Flex>
                                </form>
                            </DrawerBody>

                            <DrawerFooter>
                                <Button variant="outline" mr={3} onClick={onCloseData}>
                                    Cancelar
                                </Button>
                            </DrawerFooter>
                        </DrawerContent>
                    </Drawer>

                    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
                        <DrawerOverlay backdropFilter="blur(4px)" />
                        <DrawerContent>
                            <DrawerCloseButton />
                            <Box bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)" py={4} px={6} mb={4}>
                                <Flex align="center" gap={3}>
                                    <Flex align="center" justify="center" bg="whiteAlpha.200" borderRadius="lg" p={2}>
                                        <Icon as={TbFlame} color="white" boxSize={5} />
                                    </Flex>
                                    <Text color="white" fontWeight="bold" fontSize="lg">Configurações do Mapa</Text>
                                </Flex>
                            </Box>

                            <DrawerBody>
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();

                                        var formData = new FormData(e.target);

                                        onClose();

                                        setHeatOptions({
                                            ...Object.fromEntries(formData),
                                        });
                                    }}
                                >
                                    <Text mt={4} fontWeight={"semibold"}>
                                        Resolução
                                    </Text>
                                    <Select
                                        name="resolution"
                                        defaultValue={heatOptions.resolution}
                                    >
                                        <option value="1">1px</option>
                                        <option value="2">2px</option>
                                        <option value="3">3px</option>
                                        <option value="5">5px</option>
                                        <option value="7">7px</option>
                                        <option value="10">10px</option>
                                    </Select>

                                    <Text mt={4} fontWeight={"semibold"}>
                                        Opacidade
                                    </Text>
                                    <Select name="opacity" defaultValue={heatOptions.opacity}>
                                        <option value="0.2">20%</option>
                                        <option value="0.3">30%</option>
                                        <option value="0.4">40%</option>
                                        <option value="0.5">50%</option>
                                        <option value="0.6">60%</option>
                                        <option value="0.7">70%</option>
                                        <option value="0.8">80%</option>
                                        <option value="0.9">90%</option>
                                        <option value="1.0">100%</option>
                                    </Select>

                                    <Text mt={4} fontWeight={"semibold"}>
                                        Exponente
                                    </Text>
                                    <Select name="exp" defaultValue={heatOptions.exp}>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>
                                    </Select>

                                    <Text mt={4} fontWeight={"semibold"}>
                                        Mostrar Estações
                                    </Text>
                                    <Select
                                        name="show_stations"
                                        defaultValue={heatOptions.show_stations}
                                    >
                                        <option value="0">Não</option>
                                        <option value="1">Sim</option>
                                    </Select>

                                    <Flex mt={4} justify={"center"}>
                                        <Box>
                                            <Button colorSchema="blue" type="submit">
                                                Aplicar
                                            </Button>
                                        </Box>
                                    </Flex>
                                </form>
                            </DrawerBody>

                            <DrawerFooter>
                                <Button variant="outline" mr={3} onClick={onClose}>
                                    Cancelar
                                </Button>
                            </DrawerFooter>
                        </DrawerContent>
                    </Drawer>

                    <Flex w="full" direction="column" gap={2}>
                        <Flex
                            as={MapContainer}
                            center={center}
                            zoom={4}
                            style={{
                                minHeight: "500px",
                                height: "calc(100vh - 150px)",
                                width: "100%",
                                borderRadius: "15px !important",
                                zIndex: 0,
                                flexGrow: 1,
                            }}
                            borderRadius={"lg"}
                            attributionControl={false}
                        >
                            <TileLayer
                                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution="OpenStreetMap"
                            />

                            {!isLoadingStations && (
                                <HeatmapComponent
                                    stations={stationsData}
                                    {...heatOptions}
                                    {...dataOptions}
                                    isLoading={isLoadingHeat}
                                    setLoading={setLoadingHeat}
                                />
                            )}

                            {heatOptions.show_stations == "1" && (
                                <MarkerClusterGroup
                                    maxClusterRadius={50}
                                    disableClusteringAtZoom={7}
                                    iconCreateFunction={(cluster) => {
                                        let data = [];
                                        const childMarkers = cluster.getAllChildMarkers();

                                        childMarkers.forEach((childMarker) => {
                                            let value = childMarker.options.icon.options.className;

                                            if (value != "x") {
                                                data.push(Number(value));
                                            }
                                        });

                                        let avg = (
                                            data.reduce((a, b) => a + b, 0) / childMarkers.length
                                        ).toFixed(1);

                                        return divIcon({
                                            iconSize: [50, 30],
                                            html: `<div style="background-color: ${getBadgeColor(avg)}; color:white;" class="icone icone-cluster icone-${getBadgeColor(avg).replace("#", "")}">~${avg}<br>${cluster._childCount} estações</div>`,
                                            popupAnchor: [60, 0],
                                        });
                                    }}
                                >
                                    {renderStationMarkers(stationsData)}
                                </MarkerClusterGroup>
                            )}
                        </Flex>
                        <style>
                            {`
                .icone, .icone-cluster {
                    opacity: 0.6 !important
                }

                .leaflet-div-icon {
                    background: transparent !important;
                }
                `}
                        </style>

                        {isLoadingStations && (
                            <Flex
                                zIndex={999}
                                position={"fixed"}
                                bottom={0}
                                left={0}
                                right={0}
                                p={4}
                                justify={"center"}
                            >
                                <Flex
                                    fontSize={"sm"}
                                    className="theme-bg"
                                    align="center"
                                    justify={"center"}
                                    p={2}
                                    rounded="md"
                                    border={"1px solid"}
                                    borderColor="blue.500"
                                    shadow={"lg"}
                                    bg="white"
                                    fontWeight={"semibold"}
                                >
                                    <Spinner size="sm" />
                                    <Text ml={2}>Carregando estações...</Text>
                                </Flex>
                            </Flex>
                        )}
                        <PrintMode />
                    </Flex>
                </Box>
            </Box>
        </>
    );
}

function HeatmapComponent({
    stations,
    currentDataType,
    resolution = 5,
    opacity = 0.6,
    exp = 3,
    isLoading,
    setLoading,
}) {
    const map = useMap();
    let turf = window.turf;
    let L = window.L;

    useEffect(() => {
        // Masking everything outside Brazil
        var worldPolygon = turf.polygon([
            [
                [-180, -90],
                [180, -90],
                [180, 90],
                [-180, 90],
                [-180, -90],
            ],
        ]);
        var maskPolygon = turf.difference(worldPolygon, brazilGeoJson);

        L.geoJSON(maskPolygon, {
            style: {
                fillColor: "#ddd",
                fillOpacity: 1,
                color: "white",
                opacity: 1,
            },
        }).addTo(map);

        // Adding Brazil's GeoJSON
        L.geoJSON(brazilGeoJson, {
            style: {
                fillColor: "transparent",
                fillOpacity: 0,
                color: "transparent",
                weight: 0,
            },
        }).addTo(map);
    }, []);

    function getGradient() {
        switch (currentDataType) {
            case 'temp_avg':
            case 'temp_min':
            case 'temp_max':
                return {
                    0.0: "#805AD5",
                    0.1: '#000066',
                    0.2: '#2B6CB0',
                    0.3: '#00B5D8',
                    0.4: 'lime',
                    0.5: '#F6E05E',
                    0.6: '#ED8936',
                    0.7: '#E53E3E',
                    0.8: '#9B2C2C',
                    0.9: '#1F1F1F',
                    1.0: '#1F1F1F',
                };
            case 'humidity_avg':
            case 'humidity_min':
            case 'humidity_max':
                return {
                    0.1: "#1F1F1F",
                    0.2: "#9C4221",    // "#673721";
                    0.3: "#9B2C2C",    // "#942823";
                    0.4: "#E53E3E",    // "#CE3C35";
                    0.5: "#F6AD55",    // "#DF9D9D";
                    0.6: "#90CDF4",    // "#DDDDDD";
                    0.7: "#4299E1",    // "#9EC5E1";
                    0.8: "#2B6CB0",    // "#1673C2";
                    0.9: "#2C5282",    // "#2D458F";
                    1.0: "#6B46C1"     // "#694295";
                };
            case 'rain':
            case 'cold_hours':
                return {
                    0.0: "#FFFFFF",    // "#FFFFFF";
                    0.1: "#63B3ED",    // "#EC743B";
                    0.2: "#63B3ED",    // "#EC743B";
                    0.3: "#4299E1",    // "#EAB735";
                    0.4: "#3182CE",    // "#51A14F";
                    0.5: "#2B6CB0",    // "#4FAEAD";
                    0.6: "#2C5282",    // "#2682C5";
                    0.7: "#2A4365",    // "#2D458F";
                    0.8: "#1A365D",    // "#694295";
                    0.9: "#44337A",    // "#575757";
                    1.0: "#322659"     // "#1F1F1F";
                };
        }
    }

    useEffect(() => {
        window.map = map;
    }, [map]);

    function generateHeatMap() {
        if (window.heatmapLayer) {
            map.removeLayer(window.heatmapLayer);
        }

        setTimeout(() => {
            window.heatmapLayer = L.idwLayer(
                stations?.map((s) => {
                    return [s.lat, s.long, s.value];
                }),
                {
                    opacity: parseFloat(opacity),
                    cellSize: parseInt(resolution),
                    exp: parseInt(exp),
                    max: 40,
                    min: -5,
                    onProcessStart: () => setLoading(true),
                    onProcessEnd: () => setLoading(false),
                    gradient: getGradient()
                }
            ).addTo(window.map);
        }, 1000);
    }

    useEffect(() => {
        generateHeatMap();

        return () => {
            if (window.heatmapLayer) {
                map.removeLayer(window.heatmapLayer);
            }
        };
    }, [stations, resolution, opacity, exp]);

    // L.idwLayer(parsedStations, {opacity: 0.3, cellSize: 10, exp: 2, max: 1200}).addTo(map);

    return isLoading ? (
        <Flex
            zIndex={999}
            position={"absolute"}
            bottom={0}
            left={0}
            right={0}
            p={4}
            justify={"center"}
        >
            <Flex
                fontSize={"sm"}
                className="theme-bg"
                align="center"
                justify={"center"}
                p={2}
                rounded="md"
                border={"1px solid"}
                borderColor="blue.500"
                shadow={"lg"}
                bg="white"
                fontWeight={"semibold"}
            >
                <Spinner size="sm" />
                <Text ml={2}>Gerando mapa de calor</Text>
            </Flex>
        </Flex>
    ) : (
        <></>
    );
}
